import { useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import {
  ShoppingCartIcon,
  GlobeAltIcon,
  CreditCardIcon,
  CodeBracketIcon,
  ClipboardDocumentListIcon,
  BuildingStorefrontIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { useTranslations } from 'next-intl';

const ORBIT_DURATION = 26;

const METHODS = [
  {
    key: 'shopify',
    href: '/shopify-integration',
    icon: ShoppingCartIcon,
    accent: 'text-emerald-500'
  },
  {
    key: 'wordpress',
    href: '/wordpress-woocommerce-integration',
    icon: GlobeAltIcon,
    accent: 'text-sky-500'
  },
  {
    key: 'stripe',
    href: '/stripe-integration',
    icon: CreditCardIcon,
    accent: 'text-violet-500'
  },
  {
    key: 'custom',
    href: '/custom-api-integration',
    icon: CodeBracketIcon,
    accent: 'text-fuchsia-500'
  },
  {
    key: 'manual',
    href: '/manual-integration',
    icon: ClipboardDocumentListIcon,
    accent: 'text-amber-500'
  },
  {
    key: 'horoshop',
    href: '/horoshop-integration',
    icon: BuildingStorefrontIcon,
    accent: 'text-cyan-500'
  }
];

export default function IntegrationMethodsOrbit() {
  const t = useTranslations('integrationOrbit');
  const orbitRef = useRef(null);
  const resizeRafRef = useRef(null);
  const orbitAngleRef = useRef(0);
  const orbitRafRef = useRef(null);
  const orbitLastTsRef = useRef(0);

  const [activeKey, setActiveKey] = useState(METHODS[0].key);
  const [paused, setPaused] = useState(false);
  const [orbitRadius, setOrbitRadius] = useState(220);

  const activeMethod = useMemo(() => {
    const method = METHODS.find((item) => item.key === activeKey) || METHODS[0];
    return {
      ...method,
      title: t(`methods.${method.key}.title`),
      shortTitle: t(`methods.${method.key}.shortTitle`),
      subtitle: t(`methods.${method.key}.subtitle`),
      description: t(`methods.${method.key}.description`)
    };
  }, [activeKey, t]);

  const nodes = useMemo(() => {
    return METHODS.map((method, idx) => {
      const baseAngle = (360 / METHODS.length) * idx;
      const radians = (baseAngle * Math.PI) / 180;
      const x = Math.cos(radians) * orbitRadius;
      const y = Math.sin(radians) * orbitRadius;
      return {
        ...method,
        title: t(`methods.${method.key}.title`),
        shortTitle: t(`methods.${method.key}.shortTitle`),
        subtitle: t(`methods.${method.key}.subtitle`),
        description: t(`methods.${method.key}.description`),
        angle: baseAngle,
        x,
        y
      };
    });
  }, [orbitRadius, t]);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => {
      setActiveKey((prev) => {
        const index = METHODS.findIndex((item) => item.key === prev);
        const nextIndex = (index + 1) % METHODS.length;
        return METHODS[nextIndex].key;
      });
    }, 2200);

    return () => clearInterval(id);
  }, [paused]);

  useEffect(() => {
    const el = orbitRef.current;
    if (!el) return;

    const measure = () => {
      const size = el.clientWidth;
      const nextRadius = Math.max(120, Math.round(size * 0.34));
      setOrbitRadius((prev) => (prev === nextRadius ? prev : nextRadius));
    };

    const scheduleMeasure = () => {
      if (resizeRafRef.current) return;
      resizeRafRef.current = requestAnimationFrame(() => {
        resizeRafRef.current = null;
        measure();
      });
    };

    scheduleMeasure();
    const observer = new ResizeObserver(scheduleMeasure);
    observer.observe(el);

    return () => {
      observer.disconnect();
      if (resizeRafRef.current) {
        cancelAnimationFrame(resizeRafRef.current);
        resizeRafRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const el = orbitRef.current;
    if (!el) return;

    const degPerMs = 360 / (ORBIT_DURATION * 1000);

    const setOrbitAngle = (angleDeg) => {
      el.style.setProperty('--orbit-angle-deg', `${angleDeg}deg`);
      el.style.setProperty('--orbit-angle-neg-deg', `${-angleDeg}deg`);
    };

    if (paused) {
      if (orbitRafRef.current) {
        cancelAnimationFrame(orbitRafRef.current);
        orbitRafRef.current = null;
      }
      orbitLastTsRef.current = 0;
      return;
    }

    const tick = (ts) => {
      if (!orbitLastTsRef.current) orbitLastTsRef.current = ts;
      const delta = ts - orbitLastTsRef.current;
      orbitLastTsRef.current = ts;

      orbitAngleRef.current = (orbitAngleRef.current + delta * degPerMs) % 360;
      setOrbitAngle(orbitAngleRef.current);

      orbitRafRef.current = requestAnimationFrame(tick);
    };

    orbitRafRef.current = requestAnimationFrame(tick);

    return () => {
      if (orbitRafRef.current) {
        cancelAnimationFrame(orbitRafRef.current);
        orbitRafRef.current = null;
      }
      orbitLastTsRef.current = 0;
    };
  }, [paused]);

  return (
    <section className="relative overflow-hidden border-t border-blue-100 bg-white py-2 md:py-3">

      <div className="relative mx-auto max-w-7xl px-6">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="mt-6 text-3xl font-bold text-slate-900 md:text-4xl">
            {t('heading')}
          </h2>
          <p className="mt-2 text-sm leading-6 text-slate-600 md:text-base">
            {t('subheading')}
          </p>
        </div>

        <div className="mt-2 grid gap-4 lg:grid-cols-[1.3fr_1fr] lg:items-center">
          <div
            className="relative"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onFocusCapture={() => setPaused(true)}
            onBlurCapture={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) {
                setPaused(false);
              }
            }}
          >
            <div ref={orbitRef} className="relative mx-auto aspect-square max-w-[660px]">
              <div className="absolute inset-[8%] rounded-full border border-blue-100/70" />
              <div className="absolute inset-[20%] rounded-full border border-blue-100/70 border-dashed" />
              <div className="absolute inset-[32%] rounded-full border border-blue-100/70" />
              <div className="absolute inset-[11%] rounded-full bg-[radial-gradient(circle_at_50%_50%,rgba(59,130,246,0.22),rgba(37,99,235,0.02)_55%,rgba(255,255,255,0)_72%)]" />

              <div className="orbit-rotator">
                {nodes.map((method) => (
                  <span
                    key={`line-${method.key}`}
                    className="orbit-line"
                    style={{
                      width: `${orbitRadius}px`,
                      transform: `translate(-50%, -50%) rotate(${method.angle}deg)`
                    }}
                  />
                ))}

                {nodes.map((method) => {
                  const Icon = method.icon;
                  const isActive = activeMethod.key === method.key;

                  return (
                    <div
                      key={method.key}
                      className="orbit-node"
                      style={{
                        left: `calc(50% + ${method.x}px)`,
                        top: `calc(50% + ${method.y}px)`
                      }}
                    >
                      <div className="orbit-node-inner">
                        <Link
                          href={method.href}
                          onMouseEnter={() => setActiveKey(method.key)}
                          className={`node-link ${isActive ? 'active' : ''}`}
                          aria-label={t('aria.integration', { method: method.title })}
                        >
                          <Icon className={`node-method-icon ${method.accent}`} />
                          <span className={`node-label ${isActive ? 'active' : ''}`}>{method.title}</span>
                          <span className={`node-label-mobile ${isActive ? 'active' : ''}`}>{method.shortTitle}</span>
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="absolute inset-[36%] z-20 flex items-center justify-center">
                <div className="hub">
                  <div className="hub-logo-wrap">
                    <Image
                      src="/m_logo_6.png"
                      alt="Afivio icon"
                      fill
                      sizes="(max-width: 640px) 120px, 220px"
                      className="object-contain p-[14%]"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full rounded-3xl border border-blue-100 bg-white/90 p-6 shadow-[0_14px_38px_rgba(37,99,235,0.10)] backdrop-blur">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-600">
              {t('recommended')}
            </p>

            <h3 className="mt-2 text-3xl font-bold tracking-tight text-slate-900">
              {activeMethod.title}
            </h3>
            <p className="text-sm font-medium text-slate-500">{activeMethod.subtitle}</p>

            <p className="mt-3 max-w-md text-sm leading-7 text-slate-700 md:text-base">
              {activeMethod.description}
            </p>

            <div className="mt-5 flex flex-wrap gap-3">
              <Link
                href={activeMethod.href}
                className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
              >
                {t('openSetup', { method: activeMethod.title })} <ArrowRightIcon className="h-4 w-4" />
              </Link>
            </div>

            <p className="mt-4 text-xs text-slate-500">
              {t('tip')}
            </p>
          </div>
        </div>
      </div>

      <style jsx>{`
        .orbit-rotator {
          position: absolute;
          inset: 0;
          transform: rotate(var(--orbit-angle-deg, 0deg));
          will-change: transform;
          contain: layout paint;
        }

        .orbit-line {
          position: absolute;
          left: 50%;
          top: 50%;
          height: 1px;
          transform-origin: left center;
          background: linear-gradient(90deg, rgba(6, 182, 212, 0.32), rgba(6, 182, 212, 0.08));
        }

        .orbit-node {
          position: absolute;
          left: 50%;
          top: 50%;
          transform-origin: center;
        }

        .orbit-node-inner {
          transform: translate(-50%, -50%) rotate(var(--orbit-angle-neg-deg, 0deg));
          will-change: transform;
        }

        .node-link {
          display: inline-flex;
          flex-direction: row;
          align-items: center;
          justify-content: flex-start;
          gap: 8px;
          width: 228px;
          height: 56px;
          border-radius: 999px;
          border: 1px solid rgba(148, 163, 184, 0.32);
          background: rgba(255, 255, 255, 0.92);
          padding: 9px 14px;
          box-shadow: 0 12px 30px rgba(15, 23, 42, 0.18), 0 2px 10px rgba(37, 99, 235, 0.16);
          transition: box-shadow 180ms ease, border-color 180ms ease, background-color 180ms ease;
          white-space: nowrap;
        }

        .node-link:hover,
        .node-link.active {
          border-color: rgba(37, 99, 235, 0.45);
          background: rgba(255, 255, 255, 0.98);
          box-shadow: 0 16px 34px rgba(37, 99, 235, 0.3), 0 4px 14px rgba(15, 23, 42, 0.2);
        }

        .node-label {
          display: inline-block;
          font-size: 15px;
          font-weight: 700;
          letter-spacing: 0;
          color: #0f172a;
          white-space: nowrap;
        }

        .node-label.active {
          color: #1d4ed8;
        }

        .node-label-mobile {
          display: none;
          white-space: nowrap;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 0;
          color: #0f172a;
          line-height: 1;
        }

        .node-label-mobile.active {
          color: #1d4ed8;
        }

        .node-method-icon {
          display: block;
          flex: 0 0 auto;
          width: 18px;
          height: 18px;
        }

        .hub {
          position: relative;
          overflow: hidden;
          width: 100%;
          height: 100%;
          border-radius: 999px;
          border: 1px solid rgba(59, 130, 246, 0.24);
          background: #ffffff;
          box-shadow: 0 18px 34px rgba(37, 99, 235, 0.2), 0 6px 16px rgba(15, 23, 42, 0.16), inset 0 1px 0 rgba(255, 255, 255, 0.8);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 0;
        }

        .hub-logo-wrap {
          position: absolute;
          inset: 0;
        }

        @media (prefers-reduced-motion: reduce) {
          .orbit-rotator {
            transform: none;
          }

          .node-link {
            transform: none;
          }

          .orbit-node-inner {
            transform: translate(-50%, -50%);
          }
        }

        @media (max-width: 860px) {
          .node-link {
            width: 178px;
            height: 46px;
            padding: 7px 11px;
            gap: 7px;
          }

          .node-label {
            font-size: 12px;
          }

          .node-method-icon {
            width: 17px;
            height: 17px;
          }
        }

        @media (max-width: 640px) {
          .node-link {
            width: 92px;
            height: 44px;
            padding: 6px 8px;
            justify-content: flex-start;
            flex-direction: row;
            gap: 6px;
          }

          .node-label {
            display: none;
          }

          .node-label-mobile {
            display: block;
            font-size: 9px;
            font-weight: 700;
          }

          .node-method-icon {
            width: 16px;
            height: 16px;
          }
        }
      `}</style>
    </section>
  );
}
